"use client";

import { Eye } from "iconsax-reactjs";
import { useLocale, useTranslations } from "next-intl";
import { BlurImage } from "@/components/core";
import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import { WalletAddress } from "@/components/core/WalletAddress/WalletAddress";
import { Link } from "@/i18n/navigation";
import type { BlogCategory } from "@/lib/blog/labels";
import { useDateFormatter } from "@/lib/i18n/date";
import { brandText } from "@/lib/site-branding";
import { cn } from "@/lib/utils";
import type { components } from "@/types/api/api-types";
import { BlogBadges } from "./BlogBadges";

type BlogCardProps = components["schemas"]["BlogPostCardResponseDto"] & {
  className?: string;
};

export const BlogCard: React.FC<BlogCardProps> = ({
  slug,
  title,
  excerpt,
  cover_image_url,
  category,
  statement_status,
  tags,
  view_count,
  published_at,
  author_name,
  author_wallet,
  className,
}) => {
  const t = useTranslations("blog");
  const df = useDateFormatter();
  const locale = useLocale();
  // Statements are PUBLIC, author-published: show the real publisher
  // (nickname → wallet). The category constant is the foundation/site label and
  // applies only to articles/dao_action (which carry null author fields).
  const fallbackAuthor = t(`author.${category as BlogCategory}`);

  return (
    <Link
      href={`/blog/${slug}`}
      className={cn(
        "block rounded-2xl bg-bg-secondary p-4 transition-colors hover:bg-bg-elevated",
        className,
      )}
    >
      {cover_image_url && (
        <div className="mb-4 overflow-hidden rounded-xl">
          <BlurImage
            src={cover_image_url}
            alt={brandText(title, locale)}
            width={640}
            height={480}
            className="aspect-[4/3] w-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      <h3 className="line-clamp-2 text-base font-bold leading-7 text-text-primary">
        {brandText(title, locale)}
      </h3>

      {excerpt && (
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-text-secondary">
          {brandText(excerpt, locale)}
        </p>
      )}

      <div className="my-4 border-t border-stroke-light/40" />

      {/* Meta row — RTL: first child sits on the right. Views land on the left. */}
      <div className="flex items-center justify-between text-xs text-text-disabled">
        <span className="inline-flex items-center gap-2">
          {author_name ? (
            <span>{author_name}</span>
          ) : author_wallet ? (
            <WalletAddress
              address={author_wallet}
              size={12}
              truncateSize="sm"
              responsive={false}
              canCopy={false}
            />
          ) : (
            <span>{fallbackAuthor}</span>
          )}
          <span className="size-1 rounded-full bg-current" aria-hidden="true" />
          {published_at && <span>{df.numeric(published_at)}</span>}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Eye size={16} />
          {view_count}
        </span>
      </div>

      {/* Footer — chips on the right (first child), "read more" on the left. */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <BlogBadges
          category={category}
          statementStatus={statement_status}
          tags={tags}
        />
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-text-primary">
          {t("readMore")}
          <DirectionalChevron size={18} />
        </span>
      </div>
    </Link>
  );
};
